import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow, type SpoolTrackerWeldStepType } from '../../spoolNcrApi';
import { getRepairStripInsertionSummary } from '../../integratedRepairStrip';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Button } from '../../../../../../../components/ui/button';
import { Input } from '../../../../../../../components/ui/input';
import { Label } from '../../../../../../../components/ui/label';
import { AntdDate } from '../../../../../../../components/ui/antd-date';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../../../components/ui/dialog';
import AddNewItemDialog from '../../../../../../../components/ui/AddNewItemDialog';
import { Notification } from '../../../../../../../components/ui/custom-notification';
import { SearchableSelect } from '../../../../../../../components/ui/SearchableSelect';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../../redux/Project/materialSaga';
import { MultiSelect } from '../../../../../../../components/ui/multi-select';
import { Plus, Trash2 } from 'lucide-react';
import { WELD_WELD_TYPE_TAGS, type DocumentTag, type SpecificationDocument } from '../../../../../types';
import type { SpoolCard, WeldJoint } from '../../SpoolsProduction';
import { Signature } from '../../Signature';
import type { SignaturePanelProps } from '../weld/signatureTypes';
import { FieldGroup, FormGrid, SectionTitle } from '../weld/spoolDetailFormUi';
import {
  buildCustomerRepSignaturePayload,
  buildQcSignaturePayload,
  type SpoolStepPanelContext,
} from '../weld/spoolStepPayloadHelpers';
import type { MemberOption } from '../weld/signatureTypes';
import {
  findRepairWeldEntryInWeldingPayload,
  getLastDedicatedRepairWeldingInfo,
  getRepairWeldingPanelPayload,
  getWeldStepPanelPayload,
  resolveWeldingStepPanelRowId,
  weldPanelsRevision,
} from '../weld/weldStepPanelHydration';
import {
  nextAvailableWeldingPassName,
  WELDING_PASS_NAME_OPTIONS,
} from '../../../../../constants/weldingPassNames';
import {
  computeWeldingPassMetrics,
  createPassConfigRowsFromModal,
  formatWeldingPassMetric,
  getPassConfigurationFromPayload,
  parsePassConfigurationFromPayload,
  passConfigRowsToPayload,
  type WeldingPassConfigPayloadRow,
  type WeldingPassConfigRow,
  WELDING_PASS_TIME_MIN_SEC_LABEL,
} from '../weld/weldingPassConfig';
import { WeldingRepairEntrySection } from '../weld/WeldingRepairEntrySection';
import {
  DEFAULT_WELDING_REPAIR_ENTRY,
  readRepairEntryFromWeldingPayload,
  repairEntryToPayload,
  type WeldingRepairEntryForm,
} from '../weld/weldingRepairEntry';

const DEFAULT_WELD_TYPE_LABELS = ['Butt Weld', 'Fillet Weld', 'Socket Weld'] as const;

/** Legacy slugs + joint label text → display label; save payload uses label text. */
function normalizeWeldTypeDisplay(raw?: string): string {
  if (raw == null || String(raw).trim() === '') return 'Butt Weld';
  const s = String(raw).trim();
  const lower = s.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  if (lower === 'butt' || lower === 'butt_weld') return 'Butt Weld';
  if (lower === 'fillet' || lower === 'fillet_weld') return 'Fillet Weld';
  if (lower === 'socket' || lower === 'socket_weld') return 'Socket Weld';
  if (s === 'Butt Weld' || s === 'Fillet Weld' || s === 'Socket Weld') return s;
  const t = s.toLowerCase();
  if (t.includes('fillet')) return 'Fillet Weld';
  if (t.includes('socket')) return 'Socket Weld';
  /* Custom labels from tags API or legacy free text — keep; register-only default is butt. */
  return s;
}

/** Merge API payload variants (camelCase from UI save vs possible snake_case from DB). */
function weldingPayloadField(pl: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (k in pl && pl[k] != null) return pl[k];
  }
  return undefined;
}

/** Build `repairLink` for save when `repairFromNcr` is missing but GET returned a persisted repair row. */
function repairLinkForPayload(
  repairFromNcr: NcrRow | null,
  persistedPl: Record<string, unknown> | null | undefined,
  repairSourceStep: SpoolTrackerWeldStepType | null | undefined,
  effectiveRepairNo: number | null
): WeldingSavePayload['repairLink'] {
  if (repairFromNcr) {
    return {
      sourceNcrId: repairFromNcr.id,
      sourceNcrNo: repairFromNcr.ncrNo,
      sourceStep: String(repairSourceStep ?? repairFromNcr.weldStepType),
      repairNo: effectiveRepairNo,
    };
  }
  const rl = persistedPl?.repairLink;
  if (rl && typeof rl === 'object') {
    const o = rl as Record<string, unknown>;
    return {
      sourceNcrId: String(o.sourceNcrId ?? o.source_ncr_id ?? ''),
      sourceNcrNo: String(o.sourceNcrNo ?? o.source_ncr_no ?? ''),
      sourceStep: String(o.sourceStep ?? o.source_step ?? 'welding'),
      repairNo: effectiveRepairNo ?? (o.repairNo != null ? Number(o.repairNo) : o.repair_no != null ? Number(o.repair_no) : null),
    };
  }
  if (effectiveRepairNo != null) {
    return {
      sourceNcrId: '',
      sourceNcrNo: '',
      sourceStep: String(repairSourceStep ?? 'accept'),
      repairNo: effectiveRepairNo,
    };
  }
  return null;
}

function parseCommaSeparatedLabels(raw: string | undefined | null): string[] {
  if (raw == null || String(raw).trim() === '') return [];
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinCommaLabels(labels: string[]): string {
  return labels.join(', ');
}

/** Parse welder team-member ids from saved payload (array, JSON string, or single id). */
function parseWelderIdsFromPayload(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id)).filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      return parsed.map((id) => String(id)).filter(Boolean);
    }
  } catch {
    /* single id string */
  }
  return [s];
}

/** Persisted payload stores human-readable labels (not specification material ids). */
function resolveSpecOptionLabel(
  raw: string | undefined | null,
  options: { value: string; label: string }[]
): string {
  if (!raw?.trim()) return '';
  const r = raw.trim();
  if (options.some((o) => o.label === r)) return r;
  const byValue = options.find((o) => String(o.value) === r);
  return byValue?.label ?? r;
}

function legacyWpsSlugToLabel(
  raw: string | undefined | null,
  wpsOptions: { value: string; label: string }[]
): string {
  if (!raw?.trim()) return wpsOptions[0]?.label ?? '';
  const s = String(raw).toLowerCase();
  if (wpsOptions.length === 0) return String(raw).trim();
  if (s.includes('wps002') || (s.includes('002') && s.includes('wps'))) {
    const hit = wpsOptions.find((o) => o.label.toLowerCase().includes('002'));
    return hit?.label ?? wpsOptions[0].label;
  }
  const hit = wpsOptions.find((o) => o.label.toLowerCase().includes('001')) ?? wpsOptions[0];
  return hit.label;
}

/** Map saved welding-step payload signature blocks → `Signature` / modal shape (`customer` = welder, `qc` = QC). */
function apiWeldingSigBlockToUi(raw: unknown): {
  signature: string;
  name: string;
  comment?: string;
  stampImage?: string;
  timestamp: string;
} | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const img = String(o.signatureImage ?? o.signature ?? '').trim();
  if (!img) return undefined;
  return {
    signature: img,
    name: String(o.signerNameFromPad ?? o.selectedName ?? o.name ?? ''),
    comment: String(o.comment ?? ''),
    stampImage: String(o.stampImage ?? ''),
    timestamp: String(o.timestamp ?? new Date().toISOString()),
  };
}

/** Build `signatureData` for the welding `Signature` component from persisted draft payload only. */
function weldingPayloadToSignatureData(pl: Record<string, unknown> | null): {
  signature: Record<string, unknown>;
} {
  if (!pl || typeof pl !== 'object') return { signature: {} };
  const crs = pl.customerRepSignature ?? pl.customer_rep_signature;
  const ws = pl.welderSignature ?? pl.welder_signature;
  const qs = pl.qcSignature ?? pl.qc_signature;
  const customer = apiWeldingSigBlockToUi(crs) ?? apiWeldingSigBlockToUi(ws);
  const qc = apiWeldingSigBlockToUi(qs);
  const signature: Record<string, unknown> = {};
  if (customer) signature.customer = customer;
  if (qc) signature.qc = qc;
  return { signature };
}

function weldingPayloadCustomerRepTeamMemberId(pl: Record<string, unknown> | null): string | null {
  if (!pl || typeof pl !== 'object') return null;
  const crs = pl.customerRepSignature ?? pl.customer_rep_signature;
  const ws = pl.welderSignature ?? pl.welder_signature;
  const raw = crs ?? ws;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id =
    o.customerRepTeamMemberId ??
    o.customer_rep_team_member_id ??
    o.welderTeamMemberId ??
    o.welder_team_member_id;
  if (id == null || id === '') return null;
  return String(id);
}

/** Final submit / repair submit requires both pads already saved on the welding draft (GET payload). */
function payloadHasBothWeldingSignatures(pl: Record<string, unknown> | null): boolean {
  if (!pl || typeof pl !== 'object') return false;
  const crs = pl.customerRepSignature ?? pl.customer_rep_signature;
  const ws = pl.welderSignature ?? pl.welder_signature;
  const qs = pl.qcSignature ?? pl.qc_signature;
  const customerRaw = crs ?? ws;
  const customerImg =
    customerRaw && typeof customerRaw === 'object'
      ? String(
          (customerRaw as Record<string, unknown>).signatureImage ??
            (customerRaw as Record<string, unknown>).signature ??
            ''
        ).trim()
      : '';
  const qcImg =
    qs && typeof qs === 'object'
      ? String((qs as Record<string, unknown>).signatureImage ?? (qs as Record<string, unknown>).signature ?? '').trim()
      : '';
  return customerImg.length > 0 && qcImg.length > 0;
}

export type WeldingSubmissionMode = 'draft' | 'submit';

/** One repair cycle row matching `repairsDraft` / persisted welding payload `repairs[]`. */
export type WeldingRepairDraftRow = {
  repairNo: number;
  sourceNcrId?: string;
  sourceNcrNo?: string;
  sourceStep: string;
  repairEntry?: Record<string, unknown>;
  weldNumberInput: string;
  weldType: string;
  wps: string;
  sideADescription: string;
  sideBDescription: string;
  sideAHeatNumber: string;
  sideBHeatNumber: string;
  consumableLot: string;
  weldDate: string;
  drawingNumber?: string;
  gasLot?: string;
  wireLot?: string;
  pipeNumber?: string;
  pipeBNumber?: string;
  welderIds?: string[];
  passConfiguration?: WeldingPassConfigPayloadRow[];
};

export interface WeldingSavePayload {
  mode: WeldingSubmissionMode;
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
  weldNumberInput: string;
  weldType: string;
  wps: string;
  sideADescription: string;
  sideBDescription: string;
  sideAHeatNumber: string;
  sideBHeatNumber: string;
  consumableLot: string;
  weldDate: string;
  /** Drawing spec — saved as display label (file name), same as Production sheet. */
  drawingNumber?: string;
  /** Comma-separated gas serial labels from materials list. */
  gasLot?: string;
  /** Comma-separated wire lot labels from materials list. */
  wireLot?: string;
  /** Pipe NRI label from material traceability (same display pattern as Production sheet). */
  pipeNumber?: string;
  pipeBNumber?: string;
  /** Team member ids from welder multi-select (Production sheet pattern). */
  welderIds?: string[];
  /** Per-pass amps/volts/time/length with calculated travel speed and heat input. */
  passConfiguration?: WeldingPassConfigPayloadRow[];
  /** Repair / cut-out details when `weldingStepKind` is `repair_weld` (NCR optional). */
  repairEntry?: Record<string, unknown>;
  /** Primary weld tab vs integrated repair strip (`repair_weld`). */
  weldingStepKind?: 'welding' | 'repair_weld';
  /** When set, this save belongs to a repair-weld cycle triggered from NCR. */
  repairLink?: {
    sourceNcrId: string;
    sourceNcrNo: string;
    sourceStep: string;
    repairNo: number | null;
  } | null;
  /** Append-only repair line(s) for POST — merged server-side into welding payload `repairs[]`. */
  repairsDraft?: WeldingRepairDraftRow[] | null;
  customerRepSignature: ReturnType<typeof buildCustomerRepSignaturePayload>;
  qcSignature: ReturnType<typeof buildQcSignaturePayload>;
  /** Existing DB row id when updating primary welding or a persisted repair cycle (server multi-row welding). */
  stepPanelRowId?: number | null;
}

export interface WeldingPanelProps {
  spool: SpoolCard;
  weld?: WeldJoint | null;
  sig: SignaturePanelProps;
  context?: SpoolStepPanelContext;
  onWeldingSave?: (payload: WeldingSavePayload) => void | Promise<void>;
  onSubmittedNcrClick?: (row: NcrRow) => void;
  onOpenRaiseNcr?: () => void;
  onAddRepairWeld?: (row: NcrRow) => void;
  mode?: 'welding' | 'repair_weld';
  repairFromNcr?: NcrRow | null;
  repairNo?: number | null;
  /** When set (integrated repair), overrides NCR row step for payload link if API row is ambiguous. */
  repairSourceStep?: SpoolTrackerWeldStepType | null;
  specifications?: SpecificationDocument[];
  materials?: { gas?: unknown[]; wireLot?: unknown[]; pipes?: unknown[] };
  /** Customer representatives for the welding signature pad (Production sheet list). */
  customerRepresentativeList?: MemberOption[];
}

export function WeldingPanel({
  spool,
  weld,
  sig,
  context,
  onWeldingSave,
  onSubmittedNcrClick,
  onOpenRaiseNcr,
  onAddRepairWeld,
  mode: panelMode = 'welding',
  repairFromNcr = null,
  repairNo = null,
  repairSourceStep = null,
  specifications = [],
  materials,
  customerRepresentativeList = [],
}: WeldingPanelProps) {
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () =>
      filterNcrRowsByWeldId(ncrListAll, weld?.id ?? context?.weldId ?? null, {
        matchStepType: panelMode,
      }),
    [ncrListAll, weld?.id, context?.weldId, panelMode]
  );
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const [weldNumberInput, setWeldNumberInput] = useState('');
  const [weldType, setWeldType] = useState('Butt Weld');
  const [weldTypeTagsFromApi, setWeldTypeTagsFromApi] = useState<DocumentTag[]>([]);
  const [isAddingWeldType, setIsAddingWeldType] = useState(false);
  /** WPS display label (spec file name), not specification id. */
  const [wps, setWps] = useState('');
  const [drawingNumber, setDrawingNumber] = useState('');
  const [pipeNumber, setPipeNumber] = useState('');
  const [pipeBNumber, setPipeBNumber] = useState('');
  const [pipeSearchTerm, setPipeSearchTerm] = useState('');
  const [pipeBSearchTerm, setPipeBSearchTerm] = useState('');
  const [isPipeSelectOpen, setIsPipeSelectOpen] = useState(false);
  const [isPipeBSelectOpen, setIsPipeBSelectOpen] = useState(false);
  const [selectedWelderIds, setSelectedWelderIds] = useState<string[]>([]);
  const [selectedCustomerRepId, setSelectedCustomerRepId] = useState<string | null>(null);
  const [selectedGasLabels, setSelectedGasLabels] = useState<string[]>([]);
  const [selectedWireLabels, setSelectedWireLabels] = useState<string[]>([]);
  const [sideADescription, setSideADescription] = useState('');
  const [sideBDescription, setSideBDescription] = useState('');
  const [sideAHeatNumber, setSideAHeatNumber] = useState('');
  const [sideBHeatNumber, setSideBHeatNumber] = useState('');
  const [consumableLot, setConsumableLot] = useState('');
  const [weldDate, setWeldDate] = useState('');
  const [passConfigRows, setPassConfigRows] = useState<WeldingPassConfigRow[]>([]);
  const [addPassModalOpen, setAddPassModalOpen] = useState(false);
  const [modalPassName, setModalPassName] = useState('');
  const [modalNumberOfPasses, setModalNumberOfPasses] = useState(1);
  const [repairEntry, setRepairEntry] = useState<WeldingRepairEntryForm>(() => ({
    ...DEFAULT_WELDING_REPAIR_ENTRY,
  }));
  /** Welder + QC pads for this panel — hydrated from welding draft payload only (not shared modal QC/Fit-Up state). */
  const [localWeldingSignatureData, setLocalWeldingSignatureData] = useState<{ signature: Record<string, unknown> }>(
    () => ({ signature: {} })
  );
  const [activeSubmit, setActiveSubmit] = useState<WeldingSubmissionMode | null>(null);
  const activeSubmitRef = useRef<WeldingSubmissionMode | null>(null);
  const weldingRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'welding')
  );

  const lastDedicatedRepair = useMemo(
    () => getLastDedicatedRepairWeldingInfo(weld ?? null),
    [weld?.id, weldPanelsRevision(weld ?? null)]
  );
  const effectiveRepairNo =
    panelMode === 'repair_weld' ? repairNo ?? lastDedicatedRepair?.repairNo ?? null : null;

  /** Welding-step draft from GET/refetch — signature pads + submit gate must match this, not other modal steps. */
  const persistedWeldingPayloadForSignatures = useMemo((): Record<string, unknown> | null => {
    if (!weld) return null;
    if (panelMode === 'repair_weld') {
      if (effectiveRepairNo == null) return null;
      const row =
        getRepairWeldingPanelPayload(weld, effectiveRepairNo) ??
        findRepairWeldEntryInWeldingPayload(weld, effectiveRepairNo);
      return row && typeof row === 'object' && Object.keys(row as object).length > 0
        ? (row as Record<string, unknown>)
        : null;
    }
    const pl = getWeldStepPanelPayload(weld, 'welding');
    return pl && Object.keys(pl).length > 0 ? (pl as Record<string, unknown>) : null;
  }, [weld?.id, panelMode, effectiveRepairNo, weldPanelsRevision(weld ?? null)]);

  const wpsOptions = useMemo(() => {
    if (!specifications?.length) return [] as { value: string; label: string }[];
    return specifications
      .filter((spec) => spec.type === 'WPS')
      .map((spec) => ({
        value: String(spec.id),
        label: spec.files?.[spec.files.length - 1]?.fileName || `WPS ${spec.id}`,
      }));
  }, [specifications]);

  const drawingOptions = useMemo(() => {
    if (!specifications?.length) return [] as { value: string; label: string }[];
    return specifications
      .filter((spec) => spec.type === 'DRAWING')
      .map((spec) => ({
        value: String(spec.id),
        label: spec.files?.[spec.files.length - 1]?.fileName || `Drawing ${spec.id}`,
      }));
  }, [specifications]);

  const gasOptionsMulti = useMemo(() => {
    const gas = materials?.gas;
    if (!Array.isArray(gas)) return [] as { value: string; label: string }[];
    return gas.map((g: unknown) => {
      const gr = g as Record<string, unknown>;
      const label =
        String((gr.details as { gasSerialNumber?: string } | undefined)?.gasSerialNumber ?? '') ||
        String(gr.gasSerialNumber ?? '') ||
        `Gas ${String(gr.materialId ?? gr.id ?? '')}`;
      return { value: label, label };
    });
  }, [materials?.gas]);

  const wireLotOptionsMulti = useMemo(() => {
    const wires = materials?.wireLot;
    if (!Array.isArray(wires)) return [] as { value: string; label: string }[];
    return wires.map((w: unknown) => {
      const wr = w as Record<string, unknown>;
      const label =
        String((wr.details as { wireLotNumber?: string } | undefined)?.wireLotNumber ?? '') ||
        String(wr.lotNumber ?? '') ||
        `Wire Lot ${String(wr.materialId ?? wr.id ?? '')}`;
      return { value: label, label };
    });
  }, [materials?.wireLot]);

  const pipeOptions = useMemo(() => {
    const pipes = materials?.pipes;
    if (!Array.isArray(pipes)) return [] as { value: string; label: string; pipeNRI?: string }[];
    return pipes.map((p: unknown) => {
      const row = p as Record<string, unknown>;
      const details = row.details as { pipeNRI?: string } | undefined;
      const pipeNRI = String(details?.pipeNRI ?? row.pipeNRI ?? '').trim();
      const status = String(row.materialStatus ?? '').trim();
      const materialId = String(row.materialId ?? row.id ?? '').trim();
      const label =
        pipeNRI && status
          ? `${pipeNRI} (${status})`
          : pipeNRI || (materialId ? `Pipe ${materialId}` : 'Pipe');
      return {
        value: materialId || label,
        pipeNRI: pipeNRI || undefined,
        label,
      };
    });
  }, [materials?.pipes]);

  const filterPipeOptions = useCallback(
    (searchTerm: string, selectedOnOtherSide: string) => {
      const q = searchTerm.trim().toLowerCase();
      return pipeOptions.filter((opt) => {
        if (selectedOnOtherSide && opt.label === selectedOnOtherSide) return false;
        if (!q) return true;
        return opt.label.toLowerCase().includes(q) || (opt.pipeNRI?.toLowerCase().includes(q) ?? false);
      });
    },
    [pipeOptions],
  );

  const filteredPipeAOptions = useMemo(
    () => filterPipeOptions(pipeSearchTerm, pipeBNumber),
    [filterPipeOptions, pipeSearchTerm, pipeBNumber],
  );

  const filteredPipeBOptions = useMemo(
    () => filterPipeOptions(pipeBSearchTerm, pipeNumber),
    [filterPipeOptions, pipeBSearchTerm, pipeNumber],
  );

  const resolvePipeOptionLabel = useCallback(
    (raw: string) => {
      const byLabel = pipeOptions.find((o) => o.label === raw);
      const byNri = pipeOptions.find((o) => o.pipeNRI === raw);
      return byLabel?.label ?? byNri?.label ?? raw.trim();
    },
    [pipeOptions],
  );

  const welderOptions = useMemo(
    () =>
      sig.welderList.map((m) => ({
        value: m.value,
        label: m.label,
      })),
    [sig.welderList]
  );

  const handleWelder1Change = useCallback((value: string[]) => {
    setSelectedWelderIds(value);
  }, []);

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);

  const weldTypeSelectOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const name of DEFAULT_WELD_TYPE_LABELS) {
      map.set(name, { id: name, name });
    }
    const pid = context?.projectId;
    for (const row of weldTypeTagsFromApi) {
      const name = row.tags_name?.trim();
      if (!name) continue;
      if (pid != null && pid !== '' && row.project_id != null && String(row.project_id) !== String(pid)) {
        continue;
      }
      if (!map.has(name)) map.set(name, { id: name, name });
    }
    if (weldType && !map.has(weldType)) {
      map.set(weldType, { id: weldType, name: weldType });
    }
    return Array.from(map.values());
  }, [weldTypeTagsFromApi, weldType, context?.projectId]);

  useEffect(() => {
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      setWeldTypeTagsFromApi([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const tags = await getListMaterialCategory(WELD_WELD_TYPE_TAGS);
      if (cancelled || !Array.isArray(tags)) return;
      const forProject = tags.filter((t: DocumentTag) => String(t.project_id) === String(pid));
      setWeldTypeTagsFromApi(forProject);
    })();
    return () => {
      cancelled = true;
    };
  }, [context?.projectId]);

  const handleAddWeldType = async (name: string) => {
    const v = name.trim();
    if (!v) return;
    const pid = context?.projectId;
    if (pid == null || pid === '') {
      Notification.warning('Project required', 'Select a project context before adding a weld type.');
      setIsAddingWeldType(false);
      return;
    }
    if (weldTypeSelectOptions.some((o) => o.name.toLowerCase() === v.toLowerCase())) {
      Notification.warning(`${v} already exists`, 'Choose another name or select it from the list.');
      setIsAddingWeldType(false);
      return;
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}') as { email?: string };
    const res = await AddMaterialCategory(
      {
        tags_name: v,
        project_id: pid,
        tags_type: WELD_WELD_TYPE_TAGS,
        created_by: user.email || 'admin',
      },
      'Weld type'
    );
    if (res) {
      setWeldTypeTagsFromApi((prev) => {
        const row = res as DocumentTag;
        if (prev.some((p) => p.tags_id === row.tags_id)) return prev;
        return [...prev, row];
      });
      setWeldType(v);
    }
    setIsAddingWeldType(false);
  };

  const applyPassConfigurationFromPayload = useCallback((pl: Record<string, unknown>) => {
    const raw = getPassConfigurationFromPayload(pl);
    if (raw != null) {
      setPassConfigRows(parsePassConfigurationFromPayload(raw));
    }
  }, []);

  const openAddPassModal = useCallback(() => {
    const usedNames = passConfigRows.map((r) => r.passName).filter(Boolean);
    setModalPassName(nextAvailableWeldingPassName(usedNames));
    setModalNumberOfPasses(1);
    setAddPassModalOpen(true);
  }, [passConfigRows]);

  const confirmAddPassRows = useCallback(() => {
    const base = modalPassName.trim();
    if (!base) {
      Notification.warning('Please select a pass name.');
      return;
    }
    const count = Math.min(20, Math.max(1, Math.floor(modalNumberOfPasses) || 1));
    const newRows = createPassConfigRowsFromModal(base, count);
    setPassConfigRows((prev) => [...prev, ...newRows]);
    setAddPassModalOpen(false);
  }, [modalPassName, modalNumberOfPasses]);

  const removePassConfigRow = useCallback((index: number) => {
    setPassConfigRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updatePassConfigRow = useCallback(
    (
      index: number,
      patch: Partial<
        Pick<WeldingPassConfigRow, 'amps' | 'volts' | 'time' | 'lengthIn' | 'interpassTempC'>
      >
    ) => {
      setPassConfigRows((prev) =>
        prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
      );
    },
    []
  );

  const passConfigurationPayload = useMemo(
    () => passConfigRowsToPayload(passConfigRows),
    [passConfigRows]
  );

  /**
   * Repair weld: hydrate from persisted `welding[]` repair row after GET/refetch (incl. weld date / mDate);
   * blank template when starting a new repair cycle with no saved row yet.
   */
  useEffect(() => {
    if (panelMode !== 'repair_weld') return;
    if (!weld) {
      setWeldNumberInput('');
      setWeldType('Butt Weld');
      setWps('');
      setDrawingNumber('');
      setPipeNumber('');
      setPipeBNumber('');
      setSelectedWelderIds([]);
      setSelectedGasLabels([]);
      setSelectedWireLabels([]);
      setSideADescription('');
      setSideBDescription('');
      setSideAHeatNumber('');
      setSideBHeatNumber('');
      setConsumableLot('');
      setWeldDate('');
      setPassConfigRows([]);
      return;
    }

    const applyRepairPayloadOverlay = (pl: Record<string, unknown>) => {
      const wn = weldingPayloadField(pl, 'weldNumberInput', 'weld_number_input');
      if (wn != null) setWeldNumberInput(String(wn));

      const wt = weldingPayloadField(pl, 'weldType', 'weld_type');
      if (wt != null) setWeldType(normalizeWeldTypeDisplay(String(wt)));

      const wpsPl = weldingPayloadField(pl, 'wps');
      if (wpsPl != null) {
        const raw = String(wpsPl);
        setWps(resolveSpecOptionLabel(raw, wpsOptions) || legacyWpsSlugToLabel(raw, wpsOptions));
      }

      const dn = weldingPayloadField(pl, 'drawingNumber', 'drawing_number');
      if (dn != null) {
        const raw = String(dn);
        setDrawingNumber(resolveSpecOptionLabel(raw, drawingOptions) || raw.trim());
      }

      const pn = weldingPayloadField(pl, 'pipeNumber', 'pipe_number', 'pipeNRI', 'pipe_nri');
      if (pn != null) {
        setPipeNumber(resolvePipeOptionLabel(String(pn)));
      }
      const pbn = weldingPayloadField(pl, 'pipeBNumber', 'pipe_b_number', 'pipeBNRI', 'pipe_b_nri');
      if (pbn != null) {
        setPipeBNumber(resolvePipeOptionLabel(String(pbn)));
      }

      const welderRaw =
        weldingPayloadField(pl, 'welderIds', 'welder_ids') ??
        weldingPayloadField(pl, 'productionWeldId', 'production_weld_id');
      if (welderRaw != null) {
        const ids = parseWelderIdsFromPayload(welderRaw);
        setSelectedWelderIds(
          ids.filter((id) => welderOptions.some((o) => o.value === id))
        );
      }

      const gl = weldingPayloadField(pl, 'gasLot', 'gas_lot');
      if (gl != null) {
        setSelectedGasLabels(
          parseCommaSeparatedLabels(String(gl)).filter((lab) =>
            gasOptionsMulti.some((o) => o.value === lab || o.label === lab)
          )
        );
      }

      const wl = weldingPayloadField(pl, 'wireLot', 'wire_lot');
      if (wl != null) {
        setSelectedWireLabels(
          parseCommaSeparatedLabels(String(wl)).filter((lab) =>
            wireLotOptionsMulti.some((o) => o.value === lab || o.label === lab)
          )
        );
      }

      const sA = weldingPayloadField(pl, 'sideADescription', 'side_a_desc', 'sideADesc');
      if (sA != null) setSideADescription(String(sA));

      const sB = weldingPayloadField(pl, 'sideBDescription', 'side_b_desc', 'sideBDesc');
      if (sB != null) setSideBDescription(String(sB));

      const hA = weldingPayloadField(pl, 'sideAHeatNumber', 'side_a_heat', 'sideAHeat');
      if (hA != null) setSideAHeatNumber(String(hA));

      const hB = weldingPayloadField(pl, 'sideBHeatNumber', 'side_b_heat', 'sideBHeat');
      if (hB != null) setSideBHeatNumber(String(hB));

      const lot = weldingPayloadField(pl, 'consumableLot', 'consumable_lot', 'lot');
      if (lot != null) setConsumableLot(String(lot));

      const wd = weldingPayloadField(pl, 'weldDate', 'weld_date', 'mDate', 'm_date');
      if (wd != null) setWeldDate(String(wd));

      applyPassConfigurationFromPayload(pl);
      setRepairEntry(readRepairEntryFromWeldingPayload(pl));
    };

    const persisted =
      effectiveRepairNo != null
        ? getRepairWeldingPanelPayload(weld, effectiveRepairNo) ??
          findRepairWeldEntryInWeldingPayload(weld, effectiveRepairNo)
        : null;

    if (persisted && Object.keys(persisted).length > 0) {
      setWeldNumberInput(weld.number ?? '');
      setWeldType(normalizeWeldTypeDisplay(weld.type));
      applyRepairPayloadOverlay(persisted);
      return;
    }

    setWeldNumberInput(weld.number ?? '');
    setWeldType(normalizeWeldTypeDisplay(weld.type));
    setWps(wpsOptions[0]?.label ?? '');
    setDrawingNumber('');
    setPipeNumber('');
    setPipeBNumber('');
    setSelectedWelderIds([]);
    setSelectedGasLabels([]);
    setSelectedWireLabels([]);
    setSideADescription('');
    setSideBDescription('');
    setSideAHeatNumber('');
    setSideBHeatNumber('');
    setConsumableLot('');
    setWeldDate('');
    setPassConfigRows([]);
    setRepairEntry({ ...DEFAULT_WELDING_REPAIR_ENTRY });
  }, [
    panelMode,
    weld,
    weld?.id,
    effectiveRepairNo,
    repairNo,
    repairFromNcr?.id,
    weldPanelsRevision(weld ?? null),
    wpsOptions,
    drawingOptions,
    resolvePipeOptionLabel,
    welderOptions,
    gasOptionsMulti,
    wireLotOptionsMulti,
    applyPassConfigurationFromPayload,
  ]);

  /**
   * Primary weld: baseline from register + overlay saved welding-step payload.
   */
  useEffect(() => {
    if (panelMode === 'repair_weld') return;

    if (!weld) {
      setWeldNumberInput('');
      setWeldType('Butt Weld');
      setWps('');
      setDrawingNumber('');
      setPipeNumber('');
      setPipeBNumber('');
      setSelectedWelderIds([]);
      setSelectedGasLabels([]);
      setSelectedWireLabels([]);
      setSideADescription('');
      setSideBDescription('');
      setSideAHeatNumber('');
      setSideBHeatNumber('');
      setConsumableLot('');
      setWeldDate('');
      setPassConfigRows([]);
      return;
    }

    const applyPayloadOverlay = (pl: Record<string, unknown>) => {
      const wn = weldingPayloadField(pl, 'weldNumberInput', 'weld_number_input');
      if (wn != null) setWeldNumberInput(String(wn));

      const wt = weldingPayloadField(pl, 'weldType', 'weld_type');
      if (wt != null) setWeldType(normalizeWeldTypeDisplay(String(wt)));

      const wpsPl = weldingPayloadField(pl, 'wps');
      if (wpsPl != null) {
        const raw = String(wpsPl);
        setWps(resolveSpecOptionLabel(raw, wpsOptions) || legacyWpsSlugToLabel(raw, wpsOptions));
      }

      const dn = weldingPayloadField(pl, 'drawingNumber', 'drawing_number');
      if (dn != null) {
        const raw = String(dn);
        setDrawingNumber(resolveSpecOptionLabel(raw, drawingOptions) || raw.trim());
      }

      const pn = weldingPayloadField(pl, 'pipeNumber', 'pipe_number', 'pipeNRI', 'pipe_nri');
      if (pn != null) {
        setPipeNumber(resolvePipeOptionLabel(String(pn)));
      }
      const pbn = weldingPayloadField(pl, 'pipeBNumber', 'pipe_b_number', 'pipeBNRI', 'pipe_b_nri');
      if (pbn != null) {
        setPipeBNumber(resolvePipeOptionLabel(String(pbn)));
      }

      const welderRaw =
        weldingPayloadField(pl, 'welderIds', 'welder_ids') ??
        weldingPayloadField(pl, 'productionWeldId', 'production_weld_id');
      if (welderRaw != null) {
        const ids = parseWelderIdsFromPayload(welderRaw);
        setSelectedWelderIds(
          ids.filter((id) => welderOptions.some((o) => o.value === id))
        );
      }

      const gl = weldingPayloadField(pl, 'gasLot', 'gas_lot');
      if (gl != null) {
        setSelectedGasLabels(
          parseCommaSeparatedLabels(String(gl)).filter((lab) =>
            gasOptionsMulti.some((o) => o.value === lab || o.label === lab)
          )
        );
      }

      const wl = weldingPayloadField(pl, 'wireLot', 'wire_lot');
      if (wl != null) {
        setSelectedWireLabels(
          parseCommaSeparatedLabels(String(wl)).filter((lab) =>
            wireLotOptionsMulti.some((o) => o.value === lab || o.label === lab)
          )
        );
      }

      const sA = weldingPayloadField(pl, 'sideADescription', 'side_a_desc', 'sideADesc');
      if (sA != null) setSideADescription(String(sA));

      const sB = weldingPayloadField(pl, 'sideBDescription', 'side_b_desc', 'sideBDesc');
      if (sB != null) setSideBDescription(String(sB));

      const hA = weldingPayloadField(pl, 'sideAHeatNumber', 'side_a_heat', 'sideAHeat');
      if (hA != null) setSideAHeatNumber(String(hA));

      const hB = weldingPayloadField(pl, 'sideBHeatNumber', 'side_b_heat', 'sideBHeat');
      if (hB != null) setSideBHeatNumber(String(hB));

      const lot = weldingPayloadField(pl, 'consumableLot', 'consumable_lot', 'lot');
      if (lot != null) setConsumableLot(String(lot));

      const wd = weldingPayloadField(pl, 'weldDate', 'weld_date', 'mDate', 'm_date');
      if (wd != null) setWeldDate(String(wd));

      applyPassConfigurationFromPayload(pl);
    };

    const pl = getWeldStepPanelPayload(weld, 'welding') ?? {};

    setWeldNumberInput(weld.number ?? '');
    setWeldType(normalizeWeldTypeDisplay(weld.type));
    {
      const wRaw = weld.wps != null ? String(weld.wps) : '';
      setWps(
        wRaw
          ? resolveSpecOptionLabel(wRaw, wpsOptions) || legacyWpsSlugToLabel(wRaw, wpsOptions)
          : wpsOptions[0]?.label ?? ''
      );
    }
    setDrawingNumber('');
    setPipeNumber('');
    setPipeBNumber('');
    setSelectedWelderIds([]);
    setSelectedGasLabels([]);
    setSelectedWireLabels([]);
    setSideADescription(weld.sideADesc ?? '');
    setSideBDescription(weld.sideBDesc ?? '');
    setSideAHeatNumber(weld.sideAHeat ?? '');
    setSideBHeatNumber(weld.sideBHeat ?? '');
    setConsumableLot(weld.consumableLot ?? '');
    setWeldDate(weld.date ?? '');

    if (Object.keys(pl).length === 0) {
      setPassConfigRows([]);
      return;
    }
    applyPayloadOverlay(pl);
  }, [
    weld,
    weld?.id,
    weldPanelsRevision(weld ?? null),
    panelMode,
    wpsOptions,
    drawingOptions,
    resolvePipeOptionLabel,
    welderOptions,
    gasOptionsMulti,
    wireLotOptionsMulti,
    applyPassConfigurationFromPayload,
  ]);

  useEffect(() => {
    if (!weld) {
      setLocalWeldingSignatureData({ signature: {} });
      return;
    }
    const pl =
      panelMode === 'repair_weld'
        ? effectiveRepairNo != null
          ? getRepairWeldingPanelPayload(weld, effectiveRepairNo) ??
            findRepairWeldEntryInWeldingPayload(weld, effectiveRepairNo)
          : null
        : getWeldStepPanelPayload(weld, 'welding');
    const normalized =
      pl && typeof pl === 'object' && Object.keys(pl as object).length > 0
        ? (pl as Record<string, unknown>)
        : null;
    setLocalWeldingSignatureData(weldingPayloadToSignatureData(normalized));
    setSelectedCustomerRepId(weldingPayloadCustomerRepTeamMemberId(normalized));
  }, [weld?.id, panelMode, effectiveRepairNo, weldPanelsRevision(weld ?? null)]);

  const handleWeldingSignatureChange = useCallback(
    (data: {
      signature: string;
      name: string;
      comment?: string;
      stampImage?: string;
      type: 'customer' | 'qc';
    }) => {
      setLocalWeldingSignatureData((prev) => ({
        ...prev,
        signature: {
          ...(prev.signature ?? {}),
          [data.type]: data.signature
            ? {
                signature: data.signature,
                name: data.name,
                comment: data.comment,
                stampImage: data.stampImage,
                timestamp: new Date().toISOString(),
              }
            : undefined,
        },
      }));
    },
    []
  );

  const buildPayload = useCallback(
    (mode: WeldingSubmissionMode): WeldingSavePayload => {
      const weldingStepKind = panelMode;
      const lastDedicated = getLastDedicatedRepairWeldingInfo(weld ?? null);
      const erNo =
        panelMode === 'repair_weld' ? repairNo ?? lastDedicated?.repairNo ?? null : null;

      const persistedRepair =
        panelMode === 'repair_weld' && erNo != null
          ? getRepairWeldingPanelPayload(weld ?? null, erNo) ??
            findRepairWeldEntryInWeldingPayload(weld ?? null, erNo)
          : null;

      const link = repairLinkForPayload(repairFromNcr, persistedRepair ?? null, repairSourceStep, erNo);

      const repairEntryPayload =
        panelMode === 'repair_weld' ? repairEntryToPayload(repairEntry) : null;

      const repairDraftRow: WeldingRepairDraftRow | null =
        panelMode === 'repair_weld' && erNo != null
          ? {
              repairNo: erNo,
              sourceNcrId: link?.sourceNcrId ?? '',
              sourceNcrNo: link?.sourceNcrNo ?? '',
              sourceStep: String(link?.sourceStep ?? repairSourceStep ?? 'accept'),
              repairEntry: repairEntryPayload ?? undefined,
              weldNumberInput,
              weldType,
              wps,
              drawingNumber,
              pipeNumber,
              pipeBNumber,
              welderIds: selectedWelderIds,
              sideADescription,
              sideBDescription,
              sideAHeatNumber,
              sideBHeatNumber,
              consumableLot,
              gasLot: joinCommaLabels(selectedGasLabels),
              wireLot: joinCommaLabels(selectedWireLabels),
              weldDate,
              passConfiguration: passConfigurationPayload,
            }
          : null;

      const stepPanelRowId = resolveWeldingStepPanelRowId(
        weld ?? null,
        panelMode,
        panelMode === 'repair_weld' ? erNo ?? undefined : undefined
      );

      return {
        mode,
        weldingStepKind,
        projectId: context?.projectId ?? null,
        pipeId: context?.pipeId ?? null,
        spoolId: context?.spoolId ?? spool.id,
        spoolName: context?.spoolName ?? spool.name,
        weldId: context?.weldId ?? weld?.id ?? null,
        weldNumber: context?.weldNumber ?? weld?.number ?? null,
        weldNumberInput,
        weldType,
        wps,
        drawingNumber,
        pipeNumber,
        pipeBNumber,
        welderIds: selectedWelderIds.length > 0 ? selectedWelderIds : undefined,
        sideADescription,
        sideBDescription,
        sideAHeatNumber,
        sideBHeatNumber,
        consumableLot,
        gasLot: joinCommaLabels(selectedGasLabels),
        wireLot: joinCommaLabels(selectedWireLabels),
        weldDate,
        passConfiguration: passConfigurationPayload,
        repairEntry: repairEntryPayload ?? undefined,
        repairLink: panelMode === 'repair_weld' ? link : null,
        repairsDraft: repairDraftRow ? [repairDraftRow] : null,
        customerRepSignature: buildCustomerRepSignaturePayload({
          customerRepresentativeList,
          selectedCustomerRepId,
          signatureData: localWeldingSignatureData,
        }),
        qcSignature: buildQcSignaturePayload({
          ...sig,
          qcSignatureData: {
            signature: {
              qc: (localWeldingSignatureData.signature as Record<string, unknown> | undefined)?.qc,
            },
          },
        }),
        ...(stepPanelRowId != null ? { stepPanelRowId } : {}),
      };
    },
    [
      context,
      spool.id,
      spool.name,
      weld?.id,
      weld?.weldStepPanels,
      weld?.number,
      weldNumberInput,
      weldType,
      wps,
      drawingNumber,
      pipeNumber,
      pipeBNumber,
      selectedWelderIds,
      selectedCustomerRepId,
      customerRepresentativeList,
      sideADescription,
      sideBDescription,
      sideAHeatNumber,
      sideBHeatNumber,
      consumableLot,
      selectedGasLabels,
      selectedWireLabels,
      weldDate,
      passConfigurationPayload,
      panelMode,
      repairFromNcr,
      repairNo,
      repairSourceStep,
      repairEntry,
      sig,
      localWeldingSignatureData,
    ]
  );

  const submit = (mode: WeldingSubmissionMode) => {
    if (!onWeldingSave) return;
    const payload = buildPayload(mode);
    activeSubmitRef.current = mode;
    setActiveSubmit(mode);
    onWeldingSave(payload);
  };

  useEffect(() => {
    if (!weldingRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [weldingRemoteSaving]);

  const hasExistingWelding = useMemo(() => {
    const pl = getWeldStepPanelPayload(weld ?? null, 'welding');
    return !!pl && Object.keys(pl).length > 0;
  }, [weld?.id, weldPanelsRevision(weld ?? null)]);

  const actionText =
    panelMode === 'repair_weld'
      ? 'Save Repair Weld'
      : hasExistingWelding
        ? 'Update Weld Record'
        : 'Save Weld Record';
  const recordLoadingText =
    panelMode === 'repair_weld' ? 'Saving…' : hasExistingWelding ? 'Updating…' : 'Saving…';
  const kind = activeSubmit ?? activeSubmitRef.current;
  const savingDraft = weldingRemoteSaving && kind === 'draft';
  const savingRecord = weldingRemoteSaving && kind === 'submit';
  const draftDisabled = !weld || savingRecord;
  const bothSignaturesOnSavedDraft = payloadHasBothWeldingSignatures(persistedWeldingPayloadForSignatures);
  const recordDisabled = !weld || savingDraft || !bothSignaturesOnSavedDraft;

  return (
    <div className="grid min-h-0 w-full grid-cols-1 gap-4">
      <Card className="flex h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200 shadow-sm">
        <CardContent className="flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4">
          {ncrList.length > 0 && onSubmittedNcrClick && context?.projectId != null && context?.projectId !== '' && context?.pipeId != null && context?.pipeId !== '' ? (
            <section
              className="w-full shrink-0"
              aria-label={panelMode === 'repair_weld' ? 'NCRs for this weld, Repair Weld' : 'NCRs for this weld, Welding'}
            >
              <WorkflowNcrSummaryTable
                ncrList={ncrList}
                loading={ncrListLoading}
                step={panelMode}
                stepTitle={panelMode === 'repair_weld' ? 'Repair Weld' : 'Welding'}
                weldId={weld?.id ?? null}
                onRowClick={onSubmittedNcrClick}
                onAddRepairWeld={panelMode === 'repair_weld' ? undefined : onAddRepairWeld}
                repairPlanHint={
                  panelMode === 'repair_weld' ? undefined : getRepairStripInsertionSummary('welding')
                }
              />
            </section>
          ) : null}
          <div className="w-full min-w-0 space-y-4">
          {panelMode === 'repair_weld' ? (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
              role="status"
            >
              <span className="font-semibold">Repair No.{repairNo ?? 1}</span>
              {repairFromNcr ? (
                <span>
                  {' '}
                  — linked to NCR <span className="font-semibold">{repairFromNcr.ncrNo}</span>{' '}
                  ({String(repairSourceStep ?? repairFromNcr.weldStepType).toUpperCase()}).
                </span>
              ) : (
                <span>
                  {' '}
                  — repair / cut-out re-weld (cycle {repairNo ?? 1}).
                </span>
              )}
            </div>
          ) : null}

          {panelMode === 'repair_weld' ? (
            <WeldingRepairEntrySection
              value={repairEntry}
              onChange={setRepairEntry}
              repairNo={repairNo ?? effectiveRepairNo}
              welderOptions={welderOptions}
            />
          ) : null}

          {weld?.workflowRepair?.phase === 'awaiting_repair_weld' ? (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
              role="status"
            >
              <span className="font-semibold">Repair weld</span>
              {weld.workflowRepair?.sourceStep === 'ndt'
                ? ' — required after NDT failure or indication. Complete and submit the weld record below, then continue with Visual Inspection / NDT retest per procedure.'
                : weld.workflowRepair?.sourceStep === 'vt'
                  ? ' — required after Visual Inspection failure. Submit the weld record below, then complete VT retest.'
                  : ' — submit the weld record below when the repair is complete.'}
            </div>
          ) : null}
          
          
          <FormGrid className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FieldGroup label="Welder" required>
          <MultiSelect
                options={welderOptions}
                selected={selectedWelderIds}
                onChange={handleWelder1Change}
                placeholder="Select welder(s)..."
              />
            </FieldGroup>

            <FieldGroup label="Weld Number" required>
              <Input
                className="h-9 text-sm"
                placeholder="e.g. W-4 (per weld map)"
                value={weldNumberInput}
                onChange={(e) => setWeldNumberInput(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup label="Weld Type" required>
              <SearchableSelect
                value={weldType}
                onValueChange={setWeldType}
                placeholder="Select weld type"
                searchPlaceholder="Search weld types…"
                options={weldTypeSelectOptions}
                onAddNew={() => setIsAddingWeldType(true)}
                addNewText="Add weld type"
                className="w-full [&_button]:h-9 [&_button]:text-sm"
              />
            </FieldGroup>
            {isAddingWeldType && (
              <AddNewItemDialog
                open={isAddingWeldType}
                onOpenChange={setIsAddingWeldType}
                title="Add weld type"
                label="Weld type"
                placeholder="e.g. Branch weld"
                onAdd={handleAddWeldType}
                buttonText="Add"
                cancelText="Cancel"
              />
            )}
            <FieldGroup label="WPS Number" required>
              <Select
                value={wps || ''}
                onValueChange={(v) => setWps(v === 'none' ? '' : v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select WPS" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Clear selection</SelectItem>
                  {wpsOptions.length > 0 ? (
                    wpsOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.label}>
                        {opt.label.length > 80 ? `${opt.label.slice(0, 80)}…` : opt.label}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="_no-wps" disabled>
                      No WPS specifications found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </FieldGroup>
            <FieldGroup label="Drawing Number" required>
              <Select
                value={drawingNumber || ''}
                onValueChange={(v) => setDrawingNumber(v === 'none' ? '' : v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select drawing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Clear selection</SelectItem>
                  {drawingOptions.length > 0 ? (
                    drawingOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.label}>
                        {opt.label.length > 80 ? `${opt.label.slice(0, 80)}…` : opt.label}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="_no-drawings" disabled>
                      No drawings found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </FieldGroup>
          
          </FormGrid>
          <SectionTitle>Joint Details</SectionTitle>
          <FormGrid className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FieldGroup label="Side A — Description">
              <Input
                className="h-9 text-sm"
                value={sideADescription}
                onChange={(e) => setSideADescription(e.target.value)}
              />
            </FieldGroup>
            <FieldGroup label="Side B — Description">
              <Input
                className="h-9 text-sm"
                value={sideBDescription}
                onChange={(e) => setSideBDescription(e.target.value)}
              />
            </FieldGroup>
            <FieldGroup label="Side A Heat Number" >
              <Input
                className="h-9 text-sm"
                placeholder="Scan pipe marking"
                value={sideAHeatNumber}
                onChange={(e) => setSideAHeatNumber(e.target.value)}
              />
            </FieldGroup>
            <FieldGroup label="Side B Heat Number" >
              <Input
                className="h-9 text-sm"
                placeholder="Scan pipe marking"
                value={sideBHeatNumber}
                onChange={(e) => setSideBHeatNumber(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup label="Pipe A Number " required>
              <Select
                value={pipeNumber || ''}
                onValueChange={(v) => {
                  const next = v === 'none' ? '' : v;
                  if (next && next === pipeBNumber) {
                    Notification.warning('Welding', 'Pipe A and Pipe B cannot be the same.');
                    return;
                  }
                  setPipeNumber(next);
                  setPipeSearchTerm('');
                }}
                open={isPipeSelectOpen}
                onOpenChange={(open) => {
                  setIsPipeSelectOpen(open);
                  if (!open) setPipeSearchTerm('');
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select pipe number" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="p-2 border-b border-gray-200 flex-shrink-0">
                    <Input
                      placeholder="Search pipe..."
                      value={pipeSearchTerm}
                      onChange={(e) => {
                        e.stopPropagation();
                        setPipeSearchTerm(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          setIsPipeSelectOpen(false);
                          setPipeSearchTerm('');
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="overflow-y-auto max-h-[240px]">
                    <SelectItem value="none">Clear selection</SelectItem>
                    {filteredPipeAOptions.length > 0 ? (
                      filteredPipeAOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.label}>
                          {opt.label.length > 80 ? `${opt.label.slice(0, 80)}…` : opt.label}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="_no-pipes" disabled>
                        {pipeOptions.length > 0
                          ? 'No pipes match your search'
                          : 'No pipe materials in traceability'}
                      </SelectItem>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </FieldGroup>
            <FieldGroup label="Pipe B Number" required>
              <Select
                value={pipeBNumber || ''}
                onValueChange={(v) => {
                  const next = v === 'none' ? '' : v;
                  if (next && next === pipeNumber) {
                    Notification.warning('Welding', 'Pipe A and Pipe B cannot be the same.');
                    return;
                  }
                  setPipeBNumber(next);
                  setPipeBSearchTerm('');
                }}
                open={isPipeBSelectOpen}
                onOpenChange={(open) => {
                  setIsPipeBSelectOpen(open);
                  if (!open) setPipeBSearchTerm('');
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select pipe number" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="p-2 border-b border-gray-200 flex-shrink-0">
                    <Input
                      placeholder="Search pipe..."
                      value={pipeBSearchTerm}
                      onChange={(e) => {
                        e.stopPropagation();
                        setPipeBSearchTerm(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          setIsPipeBSelectOpen(false);
                          setPipeBSearchTerm('');
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="overflow-y-auto max-h-[240px]">
                    <SelectItem value="none">Clear selection</SelectItem>
                    {filteredPipeBOptions.length > 0 ? (
                      filteredPipeBOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.label}>
                          {opt.label.length > 80 ? `${opt.label.slice(0, 80)}…` : opt.label}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="_no-pipes" disabled>
                        {pipeOptions.length > 0
                          ? 'No pipes match your search'
                          : 'No pipe materials in traceability'}
                      </SelectItem>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </FieldGroup>
            <FieldGroup label="Gas serial numbers">
              <MultiSelect
                options={gasOptionsMulti}
                selected={selectedGasLabels}
                onChange={setSelectedGasLabels}
                placeholder="Select gas cylinder serials"
                isSearchable
                className="w-full"
              />
            </FieldGroup>
            <FieldGroup label="Wire lot numbers">
              <MultiSelect
                options={wireLotOptionsMulti}
                selected={selectedWireLabels}
                onChange={setSelectedWireLabels}
                placeholder="Select wire lots"
                isSearchable
                className="w-full"
              />
            </FieldGroup>
            <FieldGroup label="Weld Date">
              <AntdDate className="h-9 text-sm" value={weldDate} onChange={setWeldDate} />
            </FieldGroup>
          </FormGrid>

          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between gap-2">
              <SectionTitle>Pass Configuration</SectionTitle>
              <Button
                type="button"
                size="sm"
                //variant="outline"
                className="flex items-center gap-2 shrink-0"
                onClick={openAddPassModal}
              >
                <Plus className="h-4 w-4" />
                Add Pass
              </Button>
            </div>
          
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[1000px] text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs font-medium text-gray-600">
                    <th className="px-3 py-2 min-w-[140px]">
                      Pass Name <span className="text-red-500">*</span>
                    </th>
                    <th className="px-3 py-2 min-w-[100px]">Interpass temperature (°C)</th>
                    <th className="px-3 py-2">Amps</th>
                    <th className="px-3 py-2">Volts</th>
                    <th className="px-3 py-2 min-w-[120px]" title="Minutes and seconds, e.g. 8.11 = 8 min 11 sec">
                      {WELDING_PASS_TIME_MIN_SEC_LABEL}
                    </th>
                    <th className="px-3 py-2">Length (in)</th>
                    <th className="px-3 py-2">Travel Speed (ipm)</th>
                    <th className="px-3 py-2">Heat Input (kJ/in)</th>
                    <th className="px-2 py-2 w-10" aria-label="Remove pass" />
                  </tr>
                </thead>
                <tbody>
                  {passConfigRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-3 py-8 text-center text-sm text-muted-foreground"
                      >
                        No passes yet. Click Add Pass to add rows.
                      </td>
                    </tr>
                  ) : null}
                  {passConfigRows.map((row, index) => {
                    const { travelSpeedIpm, heatInputKjIn } = computeWeldingPassMetrics(row);
                    return (
                      <tr key={row.id} className="border-b last:border-b-0">
                        <td className="px-3 py-2 align-middle font-medium text-gray-800">
                          {row.passName || '—'}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="h-8 text-sm"
                            value={row.interpassTempC}
                            onChange={(e) =>
                              updatePassConfigRow(index, { interpassTempC: e.target.value })
                            }
                            placeholder="°C"
                            aria-label="Interpass temperature (°C)"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="h-8 text-sm"
                            value={row.amps}
                            onChange={(e) => updatePassConfigRow(index, { amps: e.target.value })}
                            placeholder="Amps"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="h-8 text-sm"
                            value={row.volts}
                            onChange={(e) => updatePassConfigRow(index, { volts: e.target.value })}
                            placeholder="Volts"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="h-8 text-sm"
                            value={row.time}
                            onChange={(e) => updatePassConfigRow(index, { time: e.target.value })}
                            placeholder="e.g. 8.11"
                            aria-label={WELDING_PASS_TIME_MIN_SEC_LABEL}
                          />
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="h-8 text-sm"
                            value={row.lengthIn}
                            onChange={(e) => updatePassConfigRow(index, { lengthIn: e.target.value })}
                            placeholder="Length"
                          />
                        </td>
                        <td className="px-3 py-2 align-middle text-gray-800 tabular-nums">
                          {formatWeldingPassMetric(travelSpeedIpm, 1)}
                        </td>
                        <td className="px-3 py-2 align-middle text-gray-800 tabular-nums">
                          {formatWeldingPassMetric(heatInputKjIn, 1)}
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removePassConfigRow(index)}
                            aria-label={`Remove ${row.passName || `pass ${index + 1}`}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <Dialog open={addPassModalOpen} onOpenChange={setAddPassModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add pass rows</DialogTitle>
            
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                <div className="space-y-2">
                  <Label>
                    Pass Name <span className="text-red-500">*</span>
                  </Label>
                  <Select value={modalPassName || undefined} onValueChange={setModalPassName}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select pass name" />
                    </SelectTrigger>
                    <SelectContent>
                      {WELDING_PASS_NAME_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    Number of Passes <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={modalNumberOfPasses}
                    onChange={(e) =>
                      setModalNumberOfPasses(Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 1)))
                    }
                    placeholder="Number of passes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddPassModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-[#e04530] hover:bg-[#e04530]/90 text-white"
                  onClick={confirmAddPassRows}
                >
                  Add rows
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Signature
            customerSignature={selectedCustomerRepId}
            qcSignature={sig.selectedQcId}
            customerRepresentativeList={customerRepresentativeList}
            qcList={sig.qcList}
            handleCustomerSignatureChange={setSelectedCustomerRepId}
            handleQcSignatureChange={sig.onQcChange}
            signatureData={localWeldingSignatureData}
            onSignatureChange={handleWeldingSignatureChange}
            showCustomerRep={true}
            showQc={true}
            customerRepLabel="Customer Representative"
            qcLabel="QC Inspector"
            canSign={true}
            canEdit={true}
            isEditMode={true}
            isAdminOrSuperAdmin={true}
            placeholder="Customer Representative"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              className="border-red-300 text-red-600 hover:bg-red-50"
              disabled={!weld || savingDraft || savingRecord}
              onClick={() => onOpenRaiseNcr?.()}
            >
              Fail — Raise NCR
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={draftDisabled}
              loading={savingDraft}
              loadingText="Saving draft…"
              onClick={() => void submit('draft')}
            >
              Save Draft
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-[#e04530] hover:bg-[#e04530]/90 text-white"
              disabled={recordDisabled}
              loading={savingRecord}
              loadingText={recordLoadingText}
              onClick={() => void submit('submit')}
            >
              {actionText}
            </Button>
          </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
